import { ComponentProps, DependencyList, ReactNode } from "react";
import {
  Tiptap,
  UseEditorOptions,
  useEditor as useTTEditor,
  Editor as TTEditor,
  useTiptap,
  useTiptapState,
} from "@tiptap/react";
import { Extensions, Mark, generateHTML } from "@tiptap/core";
import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { StarterKit } from "@tiptap/starter-kit";
import { OrderedList } from "@tiptap/extension-list";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";

/**
 * Editor is a wrapper around Tiptap to provide a consistent interface
 * with the components defined below.
 * @example
 * ´´´tsx
function EditorComponent() {
  // define a local state to capture the generated HTML content
  const [html, setHtml] = useState(
    "<p>Some default Text. This is not a placeholder but actual state.</p>",
  );
  // initialize the editor with the default content
  const editor = useEditor({ content: html });

  // update the HTML state whenever the editor content changes
  editor.on("update", () => {
    // the editorGenerateHTML function applies the
    // default extensions directly.
    setHtml(editorGenerateHTML(editor));
  });

  // You can use Tiptap components like BubbleMenu
  // as if you would be using Tiptap directly.
  // 
  // Build a Toolbar with what you need.
  return (
    <Editor editor={editor}>
      <BubbleMenu editor={editor}>
        <EditorBoldItalicUnderlineGroup />
      </BubbleMenu>
      <EditorToolbar>
        <EditorBold />
        <EditorItalic />
        <EditorUnderline />
        <EditorToolbarSeparator />
        <EditorBoldItalicUnderlineGroup />
        <EditorToolbarSeparator />
        <EditorBulletList />
        <EditorOrderedList />
      </EditorToolbar>
      <EditorContent />
    </Editor>
  );
}
 * ´´´
 */
export function Editor({
  className,
  children,
  ...props
}: ComponentProps<typeof Tiptap> & { className?: string }) {
  return (
    <Tiptap {...props}>
      <div className={cn("grid gap-2", className)}>{children}</div>
    </Tiptap>
  );
}

export function EditorToolbar({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-1">{children}</div>;
}

export function EditorToolbarSeparator() {
  return <div className="mx-1 h-7 border-l" />;
}

export function EditorBold() {
  const { editor } = useTiptap();
  const isBold = useTiptapState((state) =>
    state.editor ? state.editor.isActive("bold") : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => editor.chain().focus().toggleBold().run()}
      size="sm"
      variant="outline"
      className={isBold ? "bg-secondary text-secondary-foreground" : ""}
      disabled={!editor.isEditable}
    >
      <Bold />
    </Button>
  );
}

export function EditorItalic() {
  const { editor } = useTiptap();
  const isItalic = useTiptapState((state) =>
    state.editor ? state.editor.isActive("italic") : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => editor.chain().focus().toggleItalic().run()}
      size="sm"
      variant="outline"
      className={isItalic ? "bg-secondary text-secondary-foreground" : ""}
      disabled={!editor.isEditable}
    >
      <Italic />
    </Button>
  );
}

export function EditorUnderline() {
  const { editor } = useTiptap();
  const isUnderlined = useTiptapState((state) =>
    state.editor ? state.editor.isActive("underline") : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => editor.chain().focus().toggleUnderline().run()}
      size="sm"
      variant="outline"
      className={isUnderlined ? "bg-secondary text-secondary-foreground" : ""}
      disabled={!editor.isEditable}
    >
      <Underline />
    </Button>
  );
}

export function EditorBoldItalicUnderlineGroup() {
  return (
    <ButtonGroup>
      <EditorBold />
      <EditorItalic />
      <EditorUnderline />
    </ButtonGroup>
  );
}

export function EditorBulletList() {
  const { editor } = useTiptap();
  const isBulletList = useTiptapState((state) =>
    state.editor ? state.editor.isActive("bulletList") : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => editor.chain().focus().toggleBulletList().run()}
      size="sm"
      variant="outline"
      className={isBulletList ? "bg-secondary text-secondary-foreground" : ""}
      disabled={!editor.isEditable}
    >
      <List />
    </Button>
  );
}

export function EditorOrderedList() {
  const { editor } = useTiptap();
  const isOrderedList = useTiptapState((state) =>
    state.editor ? state.editor.isActive("orderedList") : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => editor.chain().focus().toggleOrderedList().run()}
      size="sm"
      variant="outline"
      className={isOrderedList ? "bg-secondary text-secondary-foreground" : ""}
      disabled={!editor.isEditable}
    >
      <ListOrdered />
    </Button>
  );
}

export const GESA_GREEN_COLOR = "#00843D";

const TextStyle = Mark.create({
  name: "textStyle",
  priority: 101,
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element) =>
          element instanceof HTMLElement ? element.style.color || null : null,
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {};
          }
          return {
            style: `color: ${attributes.color}`,
          };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "span" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
});

function isGesaGreen(colorValue: unknown): boolean {
  if (typeof colorValue !== "string") {
    return false;
  }
  const color = colorValue.trim().toLowerCase();
  return (
    color === GESA_GREEN_COLOR.toLowerCase() || color === "rgb(0, 132, 61)"
  );
}

export function EditorGesaGreen() {
  const { editor } = useTiptap();
  const isGreen = useTiptapState((state) =>
    state.editor
      ? isGesaGreen(state.editor.getAttributes("textStyle").color)
      : false,
  );

  if (!editor) {
    return null;
  }

  return (
    <Button
      onClick={() => {
        if (isGreen) {
          editor.chain().focus().unsetMark("textStyle").run();
          return;
        }
        editor
          .chain()
          .focus()
          .setMark("textStyle", { color: GESA_GREEN_COLOR })
          .run();
      }}
      size="sm"
      variant="outline"
      className={cn(
        "font-semibold",
        isGreen ? "bg-secondary text-secondary-foreground" : "",
      )}
      disabled={!editor.isEditable}
      title="Gesa Grün"
      aria-label="Gesa Grün"
      style={{ color: GESA_GREEN_COLOR }}
    >
      A
    </Button>
  );
}

export function EditorContent({
  children,
  ...props
}: ComponentProps<typeof Tiptap.Content>) {
  return <Tiptap.Content {...props}>{children}</Tiptap.Content>;
}

export const defaultExtensions = [
  // We use the StarterKit for now.
  // This includes what we need and like what
  // we need in the future. If more and more extensions
  // are added, it may be worth importing
  // every single extension by itself to have
  // better overview of what is actually used.
  StarterKit,
  TextStyle,
];

/**
 * useEditor is a wrapper around the Tiptap useEditor hook.
 * It applies some default extensions and settings.
 */
type EditorOptions = UseEditorOptions & {
  disableOrderedListInputRules?: boolean;
};

function extensionsForEditor(disableOrderedListInputRules = false): Extensions {
  if (!disableOrderedListInputRules) {
    return defaultExtensions;
  }
  return [
    StarterKit.configure({ orderedList: false }),
    OrderedList.extend({
      addInputRules() {
        return [];
      },
    }),
    TextStyle,
  ];
}

export function useEditor(options: EditorOptions, deps?: DependencyList) {
  const {
    extensions: customExtensions,
    disableOrderedListInputRules,
    ...editorOptions
  } = options;
  return useTTEditor(
    {
      extensions: [
        ...extensionsForEditor(disableOrderedListInputRules),
        ...(customExtensions ?? []),
      ],
      content: editorOptions.content,
      editorProps: {
        attributes: {
          class: "bg-transaparent rounded-md border px-3 py-1 min-h-[110px]",
          ...editorOptions.editorProps?.attributes,
        },
        ...editorOptions.editorProps,
      },
      ...editorOptions,
    },
    deps,
  );
}

/**
 * editorGenerateHTML is a helper function to generate HTML from the editor's JSON content.
 * It applies the default extensions directly, so you don't have to worry about it.
 * You can also pass custom extensions if you need to.
 *
 * editorGeneraleHTML also transforms the default empty content "<p></p>" to an empty
 * string "". This is the proper zero-value for the field and allows more consistent
 * handling in the backend and other places, checking for empty content.
 */
export function editorGenerateHTML(
  editor: TTEditor,
  customExtensions?: Extensions,
  disableOrderedListInputRules = false,
) {
  const extensions = [
    ...extensionsForEditor(disableOrderedListInputRules),
    ...(customExtensions ?? []),
  ];
  const html = generateHTML(editor.getJSON(), extensions);
  if (html === "<p></p>") {
    return "";
  }
  return html;
}
